import * as zod from "zod";

/**
 * Fetch the current session user and upgrade any invite to a user.
 * Parameters
 * ----------
 * request The request.
 * tenant_name The tenant name path parameter.
 * client The Firebase client.
 * email_service The email service.
 *
 * Returns
 * -------
 * The user.
 * @summary Me
 */
export const GetMeParams = zod.object({
  tenant_name: zod.string(),
});

export const GetMeResponse = zod.object({
  uid: zod.string(),
  name: zod.string(),
  email: zod.email(),
  created_at: zod.iso.datetime({ offset: true }),
  updated_at: zod.iso.datetime({ offset: true }),
  is_enabled: zod.boolean(),
  is_admin: zod.boolean(),
  account_type: zod.enum(["user", "service_account"]),
  permissions: zod.array(
    zod.object({
      user_uid: zod.string(),
      permission: zod.enum([
        "review",
        "publish",
        "edit",
        "read",
        "review_mix",
        "video_labelling",
        "video_label_reviewing",
        "loading_edit_proposing",
        "manage_labellers",
        "crane_driver",
        "delivery_form",
        "crane_supervisor",
        "crane_supervisor_viewer",
        "manage_overrides_catalog",
        "view_override_history",
        "edit_override_history",
        "scrap_tracking_dashboard_viewer",
        "scrap_eye_ingest",
        "scrap_eye_manager",
        "upload_file_write",
        "purchasing_admin",
        "scenario_planning",
        "analytics_admin",
        "strategy_dashboard_admin",
        "strategy_dashboard_inventory",
        "strategy_dashboard_consumption",
        "strategy_dashboard_production",
        "strategy_dashboard_quality",
        "strategy_dashboard_cost",
        "strategy_dashboard_prices",
        "strategy_dashboard_heats",
        "strategy_dashboard_scrap_chef_usage",
        "strategy_dashboard_deliveries",
        "strategy_dashboard_compliance",
        "strategy_dashboard_consumption_projection",
        "strategy_dashboard_editing",
        "strategy_dashboard_crew_comparison",
        "strategy_dashboard_schedule",
        "strategy_dashboard_savings",
        "manage_target_baskets",
        "plan_recipient",
        "render_artefact",
        "clark_user",
        "read_clark_directory",
      ]),
    })
  ),
});

/**
 * @summary Get Auth Custom Token
 */
export const GetAuthCustomTokenParams = zod.object({
  tenant_name: zod.string(),
});

export const GetAuthCustomTokenQueryParams = zod.object({
  on_behalf_of: zod.union([zod.string(), zod.null()]).optional(),
});

export const GetAuthCustomTokenResponse = zod.object({
  custom_token: zod.string(),
  firebase_api_key: zod.string(),
});

/**
 * @summary Get Users Endpoint
 */
export const GetUsersParams = zod.object({
  tenant_name: zod.string(),
});

export const GetUsersQueryParams = zod.object({
  account_types: zod
    .union([zod.array(zod.enum(["user", "service_account"])), zod.null()])
    .optional(),
});

export const GetUsersResponseItem = zod.object({
  uid: zod.string(),
  name: zod.string(),
  email: zod.email(),
  created_at: zod.iso.datetime({ offset: true }),
  updated_at: zod.iso.datetime({ offset: true }),
  is_enabled: zod.boolean(),
  is_admin: zod.boolean(),
  account_type: zod.enum(["user", "service_account"]),
  permissions: zod.array(
    zod.object({
      user_uid: zod.string(),
      permission: zod.enum([
        "review",
        "publish",
        "edit",
        "read",
        "review_mix",
        "video_labelling",
        "video_label_reviewing",
        "loading_edit_proposing",
        "manage_labellers",
        "crane_driver",
        "delivery_form",
        "crane_supervisor",
        "crane_supervisor_viewer",
        "manage_overrides_catalog",
        "view_override_history",
        "edit_override_history",
        "scrap_tracking_dashboard_viewer",
        "scrap_eye_ingest",
        "scrap_eye_manager",
        "upload_file_write",
        "purchasing_admin",
        "scenario_planning",
        "analytics_admin",
        "strategy_dashboard_admin",
        "strategy_dashboard_inventory",
        "strategy_dashboard_consumption",
        "strategy_dashboard_production",
        "strategy_dashboard_quality",
        "strategy_dashboard_cost",
        "strategy_dashboard_prices",
        "strategy_dashboard_heats",
        "strategy_dashboard_scrap_chef_usage",
        "strategy_dashboard_deliveries",
        "strategy_dashboard_compliance",
        "strategy_dashboard_consumption_projection",
        "strategy_dashboard_editing",
        "strategy_dashboard_crew_comparison",
        "strategy_dashboard_schedule",
        "strategy_dashboard_savings",
        "manage_target_baskets",
        "plan_recipient",
        "render_artefact",
        "clark_user",
        "read_clark_directory",
      ]),
    })
  ),
});
export const GetUsersResponse = zod.array(GetUsersResponseItem);

/**
 * @summary Get User By Email Endpoint
 */
export const GetUserByEmailParams = zod.object({
  tenant_name: zod.string(),
});

export const GetUserByEmailQueryParams = zod.object({
  email: zod.email(),
});

export const GetUserByEmailResponse = zod.object({
  uid: zod.string(),
  name: zod.string(),
  email: zod.email(),
  created_at: zod.iso.datetime({ offset: true }),
  updated_at: zod.iso.datetime({ offset: true }),
  is_enabled: zod.boolean(),
  is_admin: zod.boolean(),
  account_type: zod.enum(["user", "service_account"]),
  permissions: zod.array(
    zod.object({
      user_uid: zod.string(),
      permission: zod.enum([
        "review",
        "publish",
        "edit",
        "read",
        "review_mix",
        "video_labelling",
        "video_label_reviewing",
        "loading_edit_proposing",
        "manage_labellers",
        "crane_driver",
        "delivery_form",
        "crane_supervisor",
        "crane_supervisor_viewer",
        "manage_overrides_catalog",
        "view_override_history",
        "edit_override_history",
        "scrap_tracking_dashboard_viewer",
        "scrap_eye_ingest",
        "scrap_eye_manager",
        "upload_file_write",
        "purchasing_admin",
        "scenario_planning",
        "analytics_admin",
        "strategy_dashboard_admin",
        "strategy_dashboard_inventory",
        "strategy_dashboard_consumption",
        "strategy_dashboard_production",
        "strategy_dashboard_quality",
        "strategy_dashboard_cost",
        "strategy_dashboard_prices",
        "strategy_dashboard_heats",
        "strategy_dashboard_scrap_chef_usage",
        "strategy_dashboard_deliveries",
        "strategy_dashboard_compliance",
        "strategy_dashboard_consumption_projection",
        "strategy_dashboard_editing",
        "strategy_dashboard_crew_comparison",
        "strategy_dashboard_schedule",
        "strategy_dashboard_savings",
        "manage_target_baskets",
        "plan_recipient",
        "render_artefact",
        "clark_user",
        "read_clark_directory",
      ]),
    })
  ),
});

/**
 * @summary Update User Endpoint
 */
export const UpdateUserParams = zod.object({
  tenant_name: zod.string(),
});

export const UpdateUserBody = zod
  .object({
    uid: zod.string(),
    is_admin: zod.union([zod.boolean(), zod.null()]).optional(),
    is_enabled: zod.union([zod.boolean(), zod.null()]).optional(),
    permissions: zod
      .union([
        zod.record(
          zod.string(),
          zod.array(
            zod.enum([
              "review",
              "publish",
              "edit",
              "read",
              "review_mix",
              "video_labelling",
              "video_label_reviewing",
              "loading_edit_proposing",
              "manage_labellers",
              "crane_driver",
              "delivery_form",
              "crane_supervisor",
              "crane_supervisor_viewer",
              "manage_overrides_catalog",
              "view_override_history",
              "edit_override_history",
              "scrap_tracking_dashboard_viewer",
              "scrap_eye_ingest",
              "scrap_eye_manager",
              "upload_file_write",
              "purchasing_admin",
              "scenario_planning",
              "analytics_admin",
              "strategy_dashboard_admin",
              "strategy_dashboard_inventory",
              "strategy_dashboard_consumption",
              "strategy_dashboard_production",
              "strategy_dashboard_quality",
              "strategy_dashboard_cost",
              "strategy_dashboard_prices",
              "strategy_dashboard_heats",
              "strategy_dashboard_scrap_chef_usage",
              "strategy_dashboard_deliveries",
              "strategy_dashboard_compliance",
              "strategy_dashboard_consumption_projection",
              "strategy_dashboard_editing",
              "strategy_dashboard_crew_comparison",
              "strategy_dashboard_schedule",
              "strategy_dashboard_savings",
              "manage_target_baskets",
              "plan_recipient",
              "render_artefact",
              "clark_user",
              "read_clark_directory",
            ])
          )
        ),
        zod.null(),
      ])
      .optional(),
  })
  .describe(
    "DTO to update a user. A null role for a tenant corresponds\nto removing the user from the tenant."
  );

export const UpdateUserResponse = zod.object({
  uid: zod.string(),
  name: zod.string(),
  email: zod.email(),
  created_at: zod.iso.datetime({ offset: true }),
  updated_at: zod.iso.datetime({ offset: true }),
  is_enabled: zod.boolean(),
  is_admin: zod.boolean(),
  account_type: zod.enum(["user", "service_account"]),
  permissions: zod.array(
    zod.object({
      user_uid: zod.string(),
      permission: zod.enum([
        "review",
        "publish",
        "edit",
        "read",
        "review_mix",
        "video_labelling",
        "video_label_reviewing",
        "loading_edit_proposing",
        "manage_labellers",
        "crane_driver",
        "delivery_form",
        "crane_supervisor",
        "crane_supervisor_viewer",
        "manage_overrides_catalog",
        "view_override_history",
        "edit_override_history",
        "scrap_tracking_dashboard_viewer",
        "scrap_eye_ingest",
        "scrap_eye_manager",
        "upload_file_write",
        "purchasing_admin",
        "scenario_planning",
        "analytics_admin",
        "strategy_dashboard_admin",
        "strategy_dashboard_inventory",
        "strategy_dashboard_consumption",
        "strategy_dashboard_production",
        "strategy_dashboard_quality",
        "strategy_dashboard_cost",
        "strategy_dashboard_prices",
        "strategy_dashboard_heats",
        "strategy_dashboard_scrap_chef_usage",
        "strategy_dashboard_deliveries",
        "strategy_dashboard_compliance",
        "strategy_dashboard_consumption_projection",
        "strategy_dashboard_editing",
        "strategy_dashboard_crew_comparison",
        "strategy_dashboard_schedule",
        "strategy_dashboard_savings",
        "manage_target_baskets",
        "plan_recipient",
        "render_artefact",
        "clark_user",
        "read_clark_directory",
      ]),
    })
  ),
});

/**
 * @summary Sync With Firebase Endpoint
 */
export const SyncWithFirebaseParams = zod.object({
  tenant_name: zod.string(),
});

export const SyncWithFirebaseResponse = zod.void();

/**
 * @summary Logout Endpoint
 */
export const LogoutResponse = zod.void();

/**
 * @summary Set Admin Mode Endpoint
 */
export const SetAdminModeBody = zod.object({
  mode: zod.enum(["active", "paused"]),
});

export const SetAdminModeResponse = zod.void();

/**
 * @summary Request Sign In Link Endpoint
 */
export const RequestSignInLinkParams = zod.object({
  tenant_name: zod.string(),
});

export const RequestSignInLinkBody = zod.object({
  email: zod.email(),
});

export const RequestSignInLinkResponse = zod.void();

/**
 * @summary Get Clark Profile Endpoint
 */
export const GetClarkProfileParams = zod.object({
  tenant_name: zod.string(),
});

export const GetClarkProfileQueryParams = zod.object({
  email: zod.email(),
});

export const GetClarkProfileResponse = zod.object({
  user: zod.union([
    zod.object({
      uid: zod.string(),
      name: zod.string(),
      email: zod.email(),
    }),
    zod.null(),
  ]),
  description: zod.union([zod.string(), zod.null()]),
  handles: zod.array(
    zod.object({
      channel: zod.enum(["imessage"]),
      identifier: zod.string(),
    })
  ),
});

/**
 * @summary Update Clark Profile Endpoint
 */
export const UpdateClarkProfileParams = zod.object({
  tenant_name: zod.string(),
});

export const UpdateClarkProfileBody = zod.object({
  email: zod.email(),
  name: zod.string().min(1),
  description: zod.string().min(1),
  handles: zod.array(
    zod.object({
      channel: zod.enum(["imessage"]),
      identifier: zod.string(),
    })
  ),
});

export const UpdateClarkProfileResponse = zod.object({
  user: zod.union([
    zod.object({
      uid: zod.string(),
      name: zod.string(),
      email: zod.email(),
    }),
    zod.null(),
  ]),
  description: zod.union([zod.string(), zod.null()]),
  handles: zod.array(
    zod.object({
      channel: zod.enum(["imessage"]),
      identifier: zod.string(),
    })
  ),
});
